import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Center, Loader, Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { router, usePage } from '@inertiajs/react'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { serverMessage } from './utils/serverMessage'
import { StudioLayout } from '../Components/Layouts'
import { AssistantPanel } from './components/assistant/AssistantPanel'
import { CampaignSettingsModal } from './components/campaign/CampaignSettingsModal'
import { AdAgentPopover } from './components/generation/AdAgentPopover'
import { GenerationBanner } from './components/generation/GenerationBanner'
import { GenerationForm } from './components/generation/GenerationForm'
import { EditorPublishAction } from './components/layout/EditorPublishAction'
import { EditorToolbar } from './components/layout/EditorToolbar'
import { StepHeader } from './components/layout/StepHeader'
import { SchemaForm } from './components/forms/SchemaForm'
import { SchemaFormV2 } from './components/v2form/SchemaFormV2'
import { FormatSwitcher } from './components/preview/FormatSwitcher'
import { PreviewStage, presentationFor } from './components/preview/PreviewStage'
import { TemplateSetup } from './components/templates/TemplateSetup'
import { TemplateUpgrade } from './components/templates/TemplateUpgrade'
import { notifyV2 } from './components/v2/notify'
import { CheckDesignBriefButton, DesignActionModal } from '@/Partials/Studio/DesignRequest'
import { RequestAdDesignHint } from '@/Partials/Studio/AdBuilder/RequestAdDesignButton'
import { advertiserOfDetails } from './components/generation/AdvertiserSearch'
import { switchToThirdPartyCreative } from './services/api'
import { useEditorAssistant } from './hooks/useEditorAssistant'
import { useEditorFlow } from './hooks/useEditorFlow'
import { useGenerationForm, websiteAddress } from './hooks/useGenerationForm'
import { useGenerationPreparation } from './hooks/useGenerationPreparation'
import { useNavigationGuard } from './hooks/useNavigationGuard'
import { useStudioEditor } from './hooks/useStudioEditor'
import { getAtPath } from './utils/configPaths'
import { placesLanguage } from './utils/placesLanguage'
import { isUiV2 } from './utils/uiLayoutV2'
import { visibleText } from './utils/sliderValidation'
import css from './styles/EditorV2.module.css'

// An ad with no copy and no picture yet (Build without AI) keeps the Step 1 placeholder in
// the phone, as the design draws it, until the first slogan or image arrives.
function hasAdContent(schema, configuration) {
  if (!schema || !configuration) return false
  if (!(schema.collections || []).length) return true
  for (const collection of schema.collections) {
    const fields = (collection.itemSections || []).flatMap((section) => section.fields || [])
    const items = getAtPath(configuration, collection.path, []) || []
    for (const item of items) {
      for (const field of fields) {
        const value = getAtPath(item, field.path)
        if (['image-editor', 'video-editor'].includes(field.type) && value?.sourceAssetId) return true
        if (['rich-text', 'text', 'textarea'].includes(field.type) && !field.ui?.element && visibleText(value)) return true
      }
    }
  }
  return false
}

// AD-254: a Leadbuster Spec Ad's Step 1, linked to the advertiser's website or else the post.
function specAdForm(specStart) {
  const resolved = specStart.resolvedAdvertiser
  const advertiser = resolved.placeId
    ? advertiserOfDetails(resolved)
    : { place_id: `manual:${resolved.name}`, name: resolved.name, address: '', website: resolved.website, manual: true }
  return {
    advertiser,
    socialUrl: specStart.post,
    clickoutUrl: websiteAddress(advertiser.website) ? advertiser.website : specStart.post,
  }
}

export default function EditorPage({ studioEditorV2 }) {
  const { t } = useTranslation()
  const editor = useStudioEditor(studioEditorV2)
  const props = usePage().props
  const userId = props?.auth?.user?.id || 'unknown'
  const generationConfig = studioEditorV2.generation || {}
  const flow = useEditorFlow({ editor, initialAdId: studioEditorV2.adId })
  const specStart = studioEditorV2.specStart
  const form = useGenerationForm({
    userId,
    language: editor.configuration?.ad?.language,
    adId: editor.adId,
    fileMaxKb: generationConfig.fileMaxKb,
    fileMaxLabel: generationConfig.fileMaxLabel,
    initial: specStart ? specAdForm(specStart) : null,
  })
  const assistant = useEditorAssistant({
    apiBase: studioEditorV2.apiBase,
    editor,
    userId,
    enabled: Boolean(studioEditorV2.assistant?.enabled),
    models: studioEditorV2.assistant?.models || [],
  })
  const [campaignOpen, setCampaignOpen] = useState(false)
  const [chatFormOpen, setChatFormOpen] = useState(false)
  const restartRef = useRef(null)
  const navigationGuard = useNavigationGuard(editor.dirty && editor.status !== 'saving')

  const template = editor.template
  const configuration = editor.configuration
  const job = editor.generationJob
  const running = Boolean(job) && !['completed', 'failed'].includes(job.status)
  const generating = editor.status === 'generating' || running
  const busy = ['loading', 'saving', 'publishing'].includes(editor.status)
  const v2 = isUiV2(template?.schema)
  const mode = presentationFor(configuration?.format)
  const placeholder = flow.mode === 'setup' || !hasAdContent(template?.schema, configuration)
  // The agent starts reading the advertiser's sources while the form is still being filled in;
  // only while it is open, since a finished ad changes the language the form reports.
  useGenerationPreparation({
    apiBase: studioEditorV2.apiBase,
    sources: form.sources(),
    adId: editor.adId,
    enabled: (flow.mode === 'setup' || chatFormOpen) && !generating,
  })

  // A finished run (from Step 1 or the chat) becomes part of the chat thread.
  useEffect(() => {
    if (job && ['completed', 'failed'].includes(job.status)) assistant.recordGeneration(job)
  }, [assistant, job])

  // Simon, 2026-10-19 (3B): a saved ad's template, and a switch to a third-party creative, are
  // the design team's (the server refuses them to anyone else). AD-253: new ads' too.
  const designer = Boolean(studioEditorV2.designer)

  // A "Let Us Design It" request's first design starts from its briefing.
  const designStart = studioEditorV2.designStart
  useEffect(() => {
    if (!designStart || editor.adId) return
    if (designStart.advertiser) {
      form.selectAdvertiser({
        place_id: `manual:${designStart.advertiser}`,
        name: designStart.advertiser,
        address: '',
        website: designStart.website || '',
        manual: true,
      })
    }
    if (designStart.clickoutMode) form.setClickoutMode(designStart.clickoutMode)
    if (designStart.clickoutMode === 'own-website' && designStart.clickoutUrl) form.setClickoutUrl(designStart.clickoutUrl)
    if (designStart.briefing) form.setBriefing(designStart.briefing)
  }, [designStart]) // eslint-disable-line react-hooks/exhaustive-deps

  // Coming back to Step 1 from a saved ad starts from its recorded advertiser.
  useEffect(() => {
    if (flow.mode !== 'setup' || form.advertiser) return
    const recorded = editor.campaign?.advertiser
    if (recorded?.name) {
      form.selectAdvertiser({
        place_id: recorded.placeId || `manual:${recorded.name}`,
        name: recorded.name,
        address: recorded.address || '',
        website: recorded.website || '',
        phone: recorded.phone || '',
        category: recorded.category || '',
        manual: Boolean(recorded.manual || !recorded.placeId),
      })
    }
    if (editor.campaign?.clickoutMode) form.setClickoutMode(editor.campaign.clickoutMode)
    // The template's sample link (https://example.com/) is no website of the advertiser's.
    const savedClickout = configuration?.ad?.clickoutUrl
    if (editor.campaign?.clickoutMode === 'own-website' && savedClickout && savedClickout !== template?.defaults?.ad?.clickoutUrl) {
      form.restoreClickoutUrl(savedClickout)
    }
  }, [flow.mode]) // eslint-disable-line react-hooks/exhaustive-deps

  const startGeneration = useCallback(({ briefingOverride = null } = {}) => {
    const sources = form.sources()
    if (briefingOverride && !sources.briefing_text) sources.briefing_text = briefingOverride
    setChatFormOpen(false)
    editor.generateWithAi(sources, { advertiser: form.campaignAdvertiser, clickoutMode: form.clickoutMode })
  }, [editor, form])

  // A Spec Ad's AI run starts by itself, once, when the editor and its advertiser are ready.
  const specStarted = useRef(false)
  useEffect(() => {
    if (!specStart || specStarted.current || editor.adId || !configuration || busy || generating || !form.canStart) return
    specStarted.current = true
    startGeneration()
  }, [specStart, editor.adId, configuration, busy, generating, form.canStart, startGeneration])

  const buildWithoutAi = () => {
    editor.startWithoutAi({ advertiser: form.campaignAdvertiser, clickoutMode: form.clickoutMode, clickoutUrl: form.clickoutTarget })
    flow.openEditor()
  }

  const generateAgain = () => {
    if (!editor.dirty && !editor.adId) {
      flow.backToSetup()
      return
    }
    modals.openConfirmModal({
      title: t('editorV2.shell.regenerate.title'),
      centered: true,
      radius: 'lg',
      children: (
        <Text size="sm">
          {t('editorV2.shell.regenerate.body')}
        </Text>
      ),
      labels: { confirm: t('editorV2.shell.regenerate.confirm'), cancel: t('editorV2.shell.regenerate.cancel') },
      confirmProps: { color: 'smartico-pink' },
      onConfirm: flow.backToSetup,
    })
  }

  const clickoutMode = flow.mode === 'setup' ? form.clickoutMode : editor.campaign?.clickoutMode
  const withLandingpage = clickoutMode === 'landing-page'
  // AD-259: a designer finishes the design here with "Approve / Publish"; with an Instant
  // Landingpage, on its step.
  const { designAction = null } = usePage().props
  const [designActionOpen, setDesignActionOpen] = useState(false)
  const finishesDesign = Boolean(designAction) && !withLandingpage

  const continueAction = async () => {
    if (flow.mode === 'setup') {
      form.setAttempted(true)
      document.querySelector('[data-selected][autocomplete="off"]')?.focus()
      return
    }
    const saved = await editor.save()
    if (saved && finishesDesign) {
      setDesignActionOpen(true)
      return
    }
    if (saved) {
      navigationGuard.allowNext()
      router.visit(saved.publication?.databaseAdId
        ? route('studio.ad_builder.edit', {
          ad: saved.publication.databaseAdId,
          step: editor.campaign?.clickoutMode === 'landing-page' ? 'landingpage' : 'preview',
        })
        : studioEditorV2.indexUrl)
    }
  }

  const chooseThirdPartyCreative = () => {
    modals.openConfirmModal({
      title: t('editorV2.shell.thirdParty.title'),
      centered: true,
      radius: 'lg',
      children: (
        <Text size="sm">
          {t('editorV2.shell.thirdParty.body')}
          {editor.dirty ? ` ${t('editorV2.shell.thirdParty.unsavedDiscarded')}` : ''}
        </Text>
      ),
      labels: { confirm: t('editorV2.shell.thirdParty.confirm'), cancel: t('editorV2.shell.cancel') },
      confirmProps: { color: 'smartico-pink' },
      onConfirm: async () => {
        try {
          const redirect = await switchToThirdPartyCreative(studioEditorV2.apiBase, editor.adId)
          navigationGuard.allowNext()
          router.visit(redirect)
        } catch (error) {
          notifyV2({
            tone: 'error',
            title: t('editorV2.shell.thirdParty.failedTitle'),
            message: serverMessage(error?.response?.data?.message) || t('editorV2.shell.tryAgain'),
          })
        }
      },
    })
  }

  const lastStatus = useRef(editor.status)
  useEffect(() => {
    const previous = lastStatus.current
    lastStatus.current = editor.status
    if (previous !== 'saving') return
    if (editor.status === 'saved') {
      notifyV2({ tone: 'success', title: t('editorV2.shell.saved.title'), message: t('editorV2.shell.saved.message') })
    } else if (['error', 'conflict'].includes(editor.status) && editor.formError) {
      notifyV2({ tone: 'error', title: t('editorV2.shell.saveFailed'), message: editor.formError })
    }
  }, [editor.formError, editor.status, t])

  const changeFormat = (formatId) => {
    const publishedAs = editor.publication?.databaseAdId
    if (!publishedAs) {
      editor.updateValue('format.id', formatId)
      return
    }
    modals.openConfirmModal({
      title: t('editorV2.shell.changeFormat.title'),
      centered: true,
      radius: 'lg',
      children: (
        <Text size="sm">
          {t('editorV2.shell.changeFormat.body', { id: publishedAs })}
        </Text>
      ),
      labels: { confirm: t('editorV2.shell.changeFormat.confirm'), cancel: t('editorV2.shell.cancel') },
      onConfirm: () => editor.updateValue('format.id', formatId),
    })
  }

  const agent = job && flow.mode === 'setup' && (running || job.status === 'failed')
    ? (
      <AdAgentPopover
        job={job}
        agentLabel={generationConfig.agentLabel}
        agentPipeline={generationConfig.agentPipeline}
      />
    )
    : null

  // A request refused before a job exists (e.g. 422) has no job to carry its error.
  const generationError = job?.status === 'failed'
    ? (job.error || editor.formError)
    : (job ? null : editor.generationRefusal)
  const language = placesLanguage(configuration?.ad?.language || 'en')

  const renderEditorColumn = () => {
    if (!template || !configuration) return null
    const schemaProps = {
      schema: template.schema,
      templateManifest: template.manifest,
      configuration,
      errors: editor.fieldErrors,
      updateValue: editor.updateValue,
      campaign: editor.campaign,
      updateCampaign: editor.updateCampaign,
      addAsset: editor.addAsset,
      addRenditionPreview: editor.addRenditionPreview,
      assetUrls: editor.assetUrls,
      assetSourceUrls: editor.assetSourceUrls,
      selectedItems: editor.selectedItems,
      setSelectedItem: editor.setSelectedItem,
      addCollectionItem: editor.addCollectionItem,
      removeCollectionItem: editor.removeCollectionItem,
      moveCollectionItem: editor.moveCollectionItem,
      reorderCollectionItem: editor.reorderCollectionItem,
      duplicateCollectionItem: editor.duplicateCollectionItem,
    }
    if (v2) return <SchemaFormV2 {...schemaProps} />
    return (
      <>
        <TemplateSetup
          manifest={template.manifest}
          schema={template.schema}
          presets={template.presets}
          configuration={configuration}
          updateValue={editor.updateValue}
          withFormat={false}
        />
        <SchemaForm
          {...schemaProps}
          formatInPreview
          selectedSlide={editor.selectedSlide}
          setSelectedSlide={editor.setSelectedSlide}
          addSlide={editor.addSlide}
          removeSlide={editor.removeSlide}
          moveSlide={editor.moveSlide}
        />
      </>
    )
  }

  const ready = Boolean(configuration && template)
  // AD-259: the design request takes the saved ad; a new ad gets its id from this save.
  const saveBeforeDesign = (done) => editor.save()
    .then((saved) => done(saved?.publication?.databaseAdId), () => done())

  return (
    <StudioLayout pageTitle={t('editorV2.shell.editAd')} withBottomNav={false}>
      <div className={css.page}>
        <StepHeader
          homeUrl={studioEditorV2.homeUrl || '/'}
          onRefresh={() => restartRef.current?.()}
          onContinue={continueAction}
          withLandingpage={withLandingpage}
          continueDisabled={!ready || busy || generating}
          saving={editor.status === 'saving'}
          actions={(
            <CheckDesignBriefButton />
          )}
          designSteps={Boolean(designAction)}
          continueLabel={finishesDesign ? t(`designRequests.finish.${designAction.action}`) : null}
        />
        <DesignActionModal
          request={designActionOpen ? designAction : null}
          action={designAction?.action}
          onClose={() => setDesignActionOpen(false)}
          onSuccess={() => {
            navigationGuard.allowNext()
            router.visit(route('studio.design_request.index'))
          }}
        />

        {!ready ? (
          <Center mih={420}>
            <Stack align="center" gap="xs">
              {editor.status === 'error'
                ? <IconAlertTriangle size={28} color="#e03131" />
                : <Loader color="#d00b65" />}
              <Text size="sm" c="dimmed">{editor.formError || t('editorV2.shell.loadingEditor')}</Text>
            </Stack>
          </Center>
        ) : (
          <div className={css.frame} data-layout={mode === 'billboard' ? 'billboard' : undefined} data-pinned={flow.pinned ? 'true' : undefined}>
            <div className={css.formArea}>
              {flow.mode === 'setup' ? (
                <GenerationForm
                  form={form}
                  variant="page"
                  apiBase={studioEditorV2.apiBase}
                  language={language}
                  disabled={busy}
                  running={generating}
                  onStart={() => startGeneration()}
                  onBuildWithoutAi={buildWithoutAi}
                  onCancel={editor.cancelGeneration}
                  onBackToEditor={editor.adId || editor.dirty ? flow.openEditor : undefined}
                  error={generationError}
                />
              ) : (
                <>
                  <EditorToolbar
                    homeUrl={studioEditorV2.homeUrl || '/'}
                    tab={flow.tab}
                    onTabChange={flow.setTab}
                    chatEnabled={Boolean(studioEditorV2.assistant?.enabled)}
                    onGenerate={generateAgain}
                    onCampaign={() => setCampaignOpen(true)}
                    disabled={busy || generating}
                    onUndo={editor.undo}
                    onRedo={editor.redo}
                    canUndo={editor.canUndo}
                    canRedo={editor.canRedo}
                  />
                  {/* AD-259: the customer hands the ad to the design team (hint line only). */}
                  <RequestAdDesignHint
                    adId={editor.publication?.databaseAdId}
                    saveFirst={saveBeforeDesign}
                    beforeVisit={navigationGuard.allowNext}
                  />
                  {flow.banner && (
                    <GenerationBanner
                      variant={flow.banner.variant}
                      message={flow.banner.message}
                      onClose={flow.closeBanner}
                    />
                  )}
                  {editor.formError && !generating && editor.status !== 'saving' && (
                    <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                      {editor.formError}
                    </Alert>
                  )}
                  <TemplateUpgrade
                    compatibility={editor.compatibility}
                    upgradeInProgress={editor.upgradeInProgress}
                    disabled={busy}
                    onUpgrade={editor.upgradeTemplate}
                    onCancel={editor.cancelUpgrade}
                  />
                  <div style={flow.tab === 'chat' ? undefined : { display: 'none' }}>
                    <AssistantPanel
                      assistant={assistant}
                      job={job}
                      generating={editor.status === 'generating'}
                      form={form}
                      apiBase={studioEditorV2.apiBase}
                      language={language}
                      onGenerate={startGeneration}
                      onCancelGeneration={editor.cancelGeneration}
                      formOpen={chatFormOpen}
                      setFormOpen={setChatFormOpen}
                      generationError={generationError}
                      agentLabel={generationConfig.agentLabel}
                      agentPipeline={generationConfig.agentPipeline}
                    />
                  </div>
                  <div className={css.formArea} style={flow.tab === 'editor' ? undefined : { display: 'none' }}>
                    {renderEditorColumn()}
                  </div>
                </>
              )}
            </div>

            <div className={css.previewArea}>
              {flow.mode === 'editor' && (
                <FormatSwitcher
                  manifest={template.manifest}
                  offered={editor.campaign?.formats}
                  value={configuration.format?.id}
                  onChange={changeFormat}
                  disabled={busy || generating}
                />
              )}
              <PreviewStage
                previewKey={editor.previewKey}
                previewUrl={editor.previewUrl}
                configuration={editor.previewConfiguration}
                selectedSlide={editor.selectedSlide}
                onSelectSlide={editor.setSelectedSlide}
                selectedItems={editor.previewSelections}
                onSelectItem={editor.selectPreviewItem}
                protocolVersion={template.manifest.previewProtocolVersion || 1}
                onStatusChange={editor.onPreviewStatus}
                placeholder={placeholder}
                pinned={flow.pinned}
                onTogglePin={flow.togglePin}
                restartRef={restartRef}
                overlay={agent}
              />
              {flow.mode === 'editor' && <EditorPublishAction publication={editor.publication} />}
            </div>
          </div>
        )}
      </div>

      {ready && (
        <CampaignSettingsModal
          opened={campaignOpen}
          onClose={() => setCampaignOpen(false)}
          editor={editor}
          disabled={busy || generating}
          canChangeTemplate={designer}
          onThirdParty={designer && editor.publication?.databaseAdId ? chooseThirdPartyCreative : null}
        />
      )}
    </StudioLayout>
  )
}
